import { Fade, Typography } from '@material-ui/core'
import { memo } from 'react'

import { PromotionStatsRow } from '../../graphql/promotions'
import {
  formatCount,
  formatMinorWithCurrency
} from '../../helpers/promotionAmount'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import useStyles from './styles/OverviewTable.styles'

/**
 * Status labels as EXPLICIT literals, not getTranslate(row.status).
 *
 * A dynamic key is invisible twice over: promotions.test.ts derives its checked keys by
 * scanning source for getTranslate('literal') and cannot see a variable, and getTranslate
 * falls back silently to the English literal on a miss. So an untranslated status rendered
 * "Active" to a Persian audience with no test failing and no console signal — the same
 * indirection that hid the four period-preset labels.
 *
 * An unmapped value falls through to the raw string, which is visibly wrong rather than
 * silently English-shaped.
 */
export const STATUS_LABEL: Record<string, string> = {
  Draft: 'Draft',
  Active: 'Active',
  Paused: 'Paused',
  Expired: 'Expired',
  Archived: 'Archived'
}

interface OverviewTableProps {
  rows: PromotionStatsRow[]
}

/**
 * One row per promotion, with its own currency on every money figure.
 *
 * PER-ROW CURRENCY, not a column header, because the set is genuinely mixed — that is the
 * same fact that makes a scalar grand total impossible in the totals above. A reader
 * comparing two rows in different currencies must be able to see that they are not
 * comparable, and a currency stated once at the top would hide it.
 *
 * `minorUnitDigits` comes from the SERVER per row and is passed through. It is nullable: null
 * means the server does not recognise the currency, in which case the shared formatter falls
 * back to the local table and announces in dev if that is stale too.
 *
 * A div-grid rather than MUI Table, per the conventions doc — the mobile split and the
 * skeletons in this portal are built around the grid, and a `<Table>` means re-solving both.
 */
const OverviewTable: React.FC<OverviewTableProps> = ({ rows }) => {
  const classes = useStyles()

  if (rows.length === 0) {
    return (
      <Typography className={classes.empty} data-cy="OverviewEmpty">
        {getTranslate('No promotions match this filter.')}
      </Typography>
    )
  }

  return (
    /*
      ROLES ON A DIV-GRID. The portal's convention is a CSS grid rather than <table> — the
      mobile split and the skeletons are built around it — but that is a LAYOUT convention and
      it does not require dropping semantics. Without these roles a screen reader hears a
      sequence of paragraphs: "Summer Sale, Active, 142, 1,200,000 IRR", with no association
      to a header row announced once at the top. On a screen whose whole purpose is comparing
      columns, that is unusable rather than merely degraded.
      
      Explicit ARIA roles are not stripped by display:grid, unlike the native semantics of a
      real <table>, which grid DOES break — which is the other reason not to convert.
      
      The sibling PromotionsTable has none of this. Checked: its only aria attribute is a
      refresh button's label. So this is systemic across the portal's grid tables and is filed
      as such — but a new screen should not add to the pile.
    */
    <>
      {/* A visible caption, referenced — the same fix applied to the skip-reason tables.
          Leaving this aria-label-only would put the two peer screens back on exactly the axis
          that justified changing both: an h1 with no h2 beneath it. */}
      <Typography
        variant="h6"
        component="h2"
        id="promotion-performance-caption"
        className={classes.caption}>
        {getTranslate('Promotion performance')}
      </Typography>
      <div
        data-cy="OverviewTable"
        role="table"
        aria-labelledby="promotion-performance-caption">
        <div className={classes.tableHead} role="row">
          <Typography variant="body2" role="columnheader">
            {getTranslate('Promotion')}
          </Typography>
          <Typography variant="body2" role="columnheader">
            {getTranslate('Status')}
          </Typography>
          <Typography variant="body2" role="columnheader">
            {getTranslate('Applied')}
          </Typography>
          <Typography variant="body2" role="columnheader">
            {getTranslate('Discount given')}
          </Typography>
          {/*
          TWO columns, not one cell holding "3 · 45,000 IRR".
          
          The compound version put a count and a money figure behind an unlabelled separator
          under a one-word header, and a reader had to infer which was which from magnitude —
          which fails on a one-application IRR campaign where both are small integers. In RTL
          the two operands also swap visual order while the separator stays symmetric, so even
          the positional cue reverses.
        */}
          <Typography variant="body2" role="columnheader">
            {getTranslate('In flight')}
          </Typography>
          <Typography variant="body2" role="columnheader">
            {getTranslate('In-flight discount')}
          </Typography>
        </div>

        {rows.map((row) => (
          <Fade in timeout={600} key={row.promotionId}>
            <div className={classes.tableRow} role="row" data-cy="OverviewRow">
              {/*
              The name links to the promotion itself. The Overview answers "which campaigns
              are working"; the next question is always "what is this one", and the record
              page is where that is answered. A real anchor via LocalizedLink so
              middle-click and open-in-new-tab work — an operator comparing rows opens
              several.
            */}
              <Typography className={classes.name} role="cell">
                <LocalizedLink to={`/promotions/${row.promotionId}`}>
                  {row.name}
                </LocalizedLink>
              </Typography>
              <Typography className={classes.muted} role="cell">
                {getTranslate(STATUS_LABEL[row.status] ?? row.status)}
              </Typography>
              <Typography className={classes.numeric} role="cell">
                {formatCount(row.appliedCount)}
              </Typography>
              <Typography className={classes.numeric} role="cell">
                {formatMinorWithCurrency(
                  row.discountMinor,
                  row.currency,
                  row.minorUnitDigits
                )}
              </Typography>
              <Typography
                className={`${classes.numeric} ${classes.muted}`}
                role="cell">
                {formatCount(row.inFlightCount)}
              </Typography>
              <Typography
                className={`${classes.numeric} ${classes.muted}`}
                role="cell">
                {formatMinorWithCurrency(
                  row.inFlightDiscountMinor,
                  row.currency,
                  row.minorUnitDigits
                )}
              </Typography>
            </div>
          </Fade>
        ))}
      </div>
    </>
  )
}

export default memo(OverviewTable)
